/**
 * emailLint — pre-send deliverability + accessibility checks over an email's
 * blocks + rendered HTML. Pure function, returns findings the UI renders.
 * Severity: 'error' | 'warning' | 'info'.
 */
const GMAIL_CLIP_BYTES = 102 * 1024; // Gmail clips messages larger than ~102KB
const SPAMMY = /\b(free!+|100% free|act now|limited time|risk[- ]?free|winner|congratulations|cash bonus|click here now|guarantee[d]?|\$\$\$|!!!)\b/i;
const TAG_RE = /^[a-z0-9_-]+$/;
const MAX_TAG_LEN = 200;

function walk(blocks, fn) {
  (blocks || []).forEach(b => {
    fn(b);
    if (b.type === 'columns' && b.props?.columnDefs) {
      b.props.columnDefs.forEach(c => walk(c.blocks || [], fn));
    }
  });
}

export function lintEmail(blocks = [], html = '', settings = {}, subject = '') {
  const issues = [];
  const add = (severity, category, message, fix) => issues.push({ severity, category, message, fix });
  const h = String(html || '');
  const plain = h.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

  // ── Deliverability ─────────────────────────────────────────────
  const bytes = new Blob([h]).size;
  if (bytes > GMAIL_CLIP_BYTES) {
    add('warning', 'deliverability', `Email is ${(bytes / 1024).toFixed(0)}KB — Gmail clips messages over 102KB.`, 'Trim content or split into sections.');
  }

  if (!/unsubscribe/i.test(h)) {
    add('error', 'compliance', 'No “unsubscribe” link found — required by CAN-SPAM/GDPR and hurts deliverability.', 'Add a Footer block with an unsubscribe link.');
  }

  const links = (h.match(/<a\s[^>]*href=/gi) || []).length;
  if (links === 0) add('info', 'engagement', 'No links/CTA in the email.', 'Add a Button or link so readers can act.');
  if (links > 30) add('warning', 'deliverability', `${links} links — a very high link count can trip spam filters.`, 'Reduce the number of links.');

  const placeholderLinks = (h.match(/href=["'](#|https?:\/\/(example\.com|placeholder))/gi) || []).length;
  if (placeholderLinks) add('warning', 'quality', `${placeholderLinks} placeholder/empty link(s) (href="#" or example.com).`, 'Point every link at a real URL.');

  // Image-to-text ratio (spam heuristic)
  const imgCount = (h.match(/<img\s/gi) || []).length;
  if (imgCount > 0 && plain.length < 120) {
    add('warning', 'deliverability', 'Image-heavy with little text — image-only emails often land in spam and break when images are blocked.', 'Add meaningful text alongside images.');
  }

  if (!settings.preheaderText) {
    add('info', 'engagement', 'No preheader text — inboxes will show a random snippet as the preview.', 'Set preheader text in Page Settings.');
  }

  // ── Accessibility ──────────────────────────────────────────────
  let imagesMissingAlt = 0, imageBlocks = 0;
  walk(blocks, b => {
    if (b.type === 'image') { imageBlocks++; if (!b.props?.alt || !String(b.props.alt).trim()) imagesMissingAlt++; }
    if (b.type === 'headerBar' && b.props?.logoSrc && !String(b.props.logoAlt || '').trim()) imagesMissingAlt++;
  });
  if (imagesMissingAlt) {
    add('warning', 'accessibility', `${imagesMissingAlt} image(s) missing alt text — screen readers and image-blocked inboxes show nothing.`, 'Add descriptive alt text to every image.');
  }

  const hasHeading = (blocks || []).some(b => b.type === 'heading');
  if ((blocks || []).length > 2 && !hasHeading) {
    add('info', 'accessibility', 'No heading block — headings improve structure and scannability.', 'Add a Heading to anchor the message.');
  }

  // ── Subject line (when available) ──────────────────────────────
  if (subject != null && subject !== '') {
    if (subject.length > 60) add('info', 'engagement', `Subject is ${subject.length} chars — many inboxes truncate past ~50–60.`, 'Shorten the subject.');
    if (SPAMMY.test(subject)) add('warning', 'deliverability', 'Subject contains spam-trigger words.', 'Avoid “free!!!”, “$$$”, “act now”, excessive punctuation.');
  }

  // ── Click tracking (SES link tags) ───────────────────────────────
  // One entry per anchor the designer can put a tracking id on — Button,
  // Image/Countdown links, every header-bar anchor, Story/Video's thumbnail +
  // CTA, and the Footer's unsubscribe link. Social Icons is deliberately
  // excluded: its ids are computed at export time, not stored, so there's
  // nothing here for the author to leave blank or mistype.
  const trackingEntries = [];
  const track = (id, noTrack, label) => trackingEntries.push({ id, noTrack, label });
  walk(blocks, b => {
    const pr = b.props || {};
    if (b.type === 'button') track(pr.trackingId, pr.noTrack, `Button “${pr.label || 'Click Here'}”`);
    if (b.type === 'image' && pr.link) track(pr.trackingId, pr.noTrack, `Image link (${pr.alt || 'untitled'})`);
    if (b.type === 'countdown' && pr.link) track(pr.trackingId, pr.noTrack, 'Countdown link');
    if (b.type === 'story') {
      track(pr.trackingId, pr.noTrack, `Story button “${pr.ctaLabel || 'View Story'}”`);
      if (pr.poster) track(pr.thumbnailTrackingId, pr.thumbnailNoTrack, `Story thumbnail (${pr.storyTitle || 'untitled'})`);
    }
    if (b.type === 'video') {
      track(pr.trackingId, pr.noTrack, 'Video “Watch video” link');
      track(pr.thumbnailTrackingId, pr.thumbnailNoTrack, 'Video thumbnail');
    }
    if (b.type === 'headerBar') {
      if (pr.logoLink) track(pr.logoTrackingId, pr.logoNoTrack, 'Header logo link');
      (pr.links || []).forEach(l => { if (l?.url) track(l.trackingId, l.noTrack, `Header nav “${l.label || l.alt || 'link'}”`); });
    }
    if (b.type === 'footer' && pr.unsubscribeUrl) track(pr.unsubscribeTrackingId, pr.unsubscribeNoTrack, 'Footer unsubscribe link');
  });

  const seen = new Map();
  trackingEntries.forEach(({ id, noTrack, label }) => {
    if (noTrack) return; // excluded from tracking on purpose — nothing to check
    if (!id) {
      add('warning', 'tracking', `${label} has no tracking ID.`, 'Open the block and fill in (or auto-fill) a Tracking ID.');
      return;
    }
    if (id.includes(',') || !TAG_RE.test(id)) {
      add('error', 'tracking', `Tracking ID “${id}” on ${label} contains characters SES will reject.`, 'Use only lowercase letters, digits, underscores and dashes — no commas.');
    } else if (id.length > MAX_TAG_LEN) {
      add('error', 'tracking', `Tracking ID on ${label} is ${id.length} characters — SES allows at most ${MAX_TAG_LEN}.`, 'Shorten the Tracking ID.');
    }
    seen.set(id, [...(seen.get(id) || []), label]);
  });
  seen.forEach((labels, id) => {
    if (labels.length > 1) {
      add('warning', 'tracking', `${labels.length} links share the tracking ID “${id}”: ${labels.join(', ')}.`, 'Clicks for these links will be combined — give each its own Tracking ID if that is not intended.');
    }
  });

  // A stray `ses:tags="b:"` in the rendered HTML means something upstream
  // handed the serializer an id that sanitized down to nothing — should be
  // unreachable given the panel's own sanitize-on-type + fallback behaviour,
  // but checked here too since this is the last line of defense before send.
  if (/ses:tags=["']b:["']/.test(h)) {
    add('error', 'tracking', 'An exported link has an empty ses:tags value.', 'Re-open the affected block and re-enter its Tracking ID.');
  }

  // Links missing a URL scheme (e.g. "www.foo.com" or "foo.com" instead of
  // "https://www.foo.com") often fail to rewrite as SES tracking links.
  const hrefsNoScheme = [...h.matchAll(/href=["']([^"']+)["']/gi)]
    .map(m => m[1])
    .filter(href => href && href !== '#' && !/^(https?:|mailto:|tel:|\{|\$)/i.test(href) && /^[\w-]+(\.[\w-]+)+/.test(href));
  if (hrefsNoScheme.length) {
    add('warning', 'tracking', `${hrefsNoScheme.length} link(s) are missing a URL scheme (e.g. "www.foo.com" instead of "https://www.foo.com").`, 'Add "https://" to the front of each link — these often fail to rewrite as tracking links.');
  }

  const counts = issues.reduce((a, i) => { a[i.severity] = (a[i.severity] || 0) + 1; return a; }, {});
  const score = Math.max(0, 100 - (counts.error || 0) * 25 - (counts.warning || 0) * 8 - (counts.info || 0) * 2);
  return { issues, counts, score, sizeKB: Math.round(bytes / 1024), links, images: imageBlocks };
}

export default lintEmail;
